import { ORDER_STATUSES, PaymentStatus } from '@/utils/constants'

export const capitalizeFirstLetter = (text: string | null = ''): string => {
  if (text && text.toString().length > 0) {
    return text.charAt(0).toUpperCase() + text.slice(1)
  } else {
    return ''
  }
}

export const pluralize = (quantity: number, word: string) => {
  if (quantity > 1) {
    return `${word}s`
  } else {
    return word
  }
}

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    return false
  }
}

export const getOrderStatusColor = (status: string): string => {
  const defaultColor = 'bg-dashboard-yellow'
  if (!status) return defaultColor

  const colorMap: { [key: string]: string } = {
    [ORDER_STATUSES.COMPLETED]: 'bg-dashboard-success-green',
    [ORDER_STATUSES.REFUNDED]: 'bg-dashboard-action-blue',
    [ORDER_STATUSES.PARTIALLY_REFUNDED]: 'bg-dashboard-action-blue',
    [ORDER_STATUSES.CANCELLED]: 'bg-dashboard-pink',
    [ORDER_STATUSES.UNCAPTURED]: 'bg-dashboard-error-red'
  }

  return colorMap[status] || defaultColor
}

export const getPaymentStatusColor = (status: string): string => {
  const defaultColor = 'bg-dashboard-yellow'
  if (!status) return defaultColor

  const colorMap: { [key: string]: string } = {
    [PaymentStatus.COMPLETED]: 'bg-dashboard-success-green',
    [PaymentStatus.PROCESSING]: 'bg-dashboard-yellow',
    [PaymentStatus.FAILED]: 'bg-dashboard-error-red',
    [PaymentStatus.CANCELLED]: 'bg-dashboard-pink'
  }

  return colorMap[status] || defaultColor
}

export const humanize = (text: string | null = ''): string => {
  if (!text) return ''

  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // add space between words in camelCase
    .replace(/[_-]/g, ' ')
}

export const titleCase = (text: string = ''): string => {
  return humanize(text)
    .split(' ')
    .map((word) => capitalizeFirstLetter(word))
    .join(' ')
}

export const formatNumberWithCommas = (string: string): string => {
  return string.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const formatDecimalNumber = (value: string | number, decimalPlaces = 2): string => {
  const stringValue = value.toString()

  const sanitizedValue = stringValue.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')

  const parts = sanitizedValue.split('.')
  if (parts[1]?.length > decimalPlaces) {
    parts[1] = parts[1].substring(0, decimalPlaces)
  }

  return parts.join('.')
}

export const formatMoney = (value: string | number): string => {
  const numericValue = Number(Number(value).toFixed(2))
  return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericValue)
}

export const isValidIpAddress = (ip: string): boolean => {
  // Regular expression for IPv4 validation
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/

  if (!ipv4Regex.test(ip)) return false

  // Check if each octet is valid (0-255)
  const octets = ip.split('.')
  return octets.every((octet) => {
    const num = parseInt(octet, 10)
    return num >= 0 && num <= 255
  })
}

export const isValidPort = (port: string): boolean => {
  if (!port) return true // Empty port is valid as it's optional
  const portNumber = parseInt(port, 10)
  return !isNaN(portNumber) && portNumber >= 1024 && portNumber <= 65535
}

export const snakeCaseToSentenceCase = (str: string) => {
  return capitalizeFirstLetter(str.replace(/_/g, ' '))
}

/**
 * Converts a string to a slug
 * @param str - The string to convert
 * @returns The converted string
 */
export const slugify = (str: string): string => {
  return str
    .trim() // remove leading/trailing spaces
    .toLowerCase() // lowercase everything
    .replace(/[^a-z0-9]+/g, '_') // replace non-alphanumeric with "_"
    .replace(/^_+|_+$/g, '') // trim leading/trailing "_"
    .replace(/_+/g, '_') // collapse multiple "_"
}

/**
 * Validates if a string contains only alphanumeric characters (letters and numbers)
 * @param value - The string to validate
 * @returns True if the string contains only alphanumeric characters, false otherwise
 */
export const isAlphanumeric = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false
  return /^[a-zA-Z0-9]+$/.test(value.trim())
}

/**
 * Truncates text to a specified length and appends ellipsis if needed
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncate = (text: string | undefined, maxLength: number): string => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export default {
  pluralize,
  capitalizeFirstLetter,
  copyToClipboard,
  getOrderStatusColor,
  formatNumberWithCommas,
  formatDecimalNumber,
  isValidIpAddress,
  isValidPort,
  isAlphanumeric,
  slugify,
  truncate
}
